<script setup lang="ts">
import { Plus, Trash } from "@iconoir/vue";
import { useCrud } from "~/composables/use-crud";
import type { BillingCycle, WorkspacePlan, PlanPrice, PlanFeature, PlanLimit } from "@artech-global/types";
import { deburr } from "lodash-es";

defineOptions({ name: "WorkspacePlanFormModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const toast = useToast();

const isOpen = ref(false);

const {
  formModel,
  formErrors,
  errorMsg,
  isCreate,
  saveLoading,
  save,
  load,
  reset,
} = useCrud<WorkspacePlan>({
  endpoint: "/workspace-plans",
  defaultFormModel: () =>
    ({
      id: "",
      name: "",
      key: "",
      type: "free",
      kind: "event-only",
      position: 0,
      isDefault: false,
      isPublic: true,
      trialDays: 0,
      currency: "USD",
      price: 0,
      defaultBillingCycle: "monthly" as BillingCycle,
      prices: [] as PlanPrice[],
      features: [] as PlanFeature[],
      limits: [] as PlanLimit[],
    }) as unknown as WorkspacePlan,
});

const typeOptions = computed(() => [
  { value: "free", label: t("workspacePlan.type.free") },
  { value: "paid", label: t("workspacePlan.type.paid") },
]);

const kindOptions = computed(() => [
  { value: "event-only", label: t("workspacePlan.kind.eventOnly") },
  { value: "store", label: t("workspacePlan.kind.store") },
]);

const currencyOptions = [
  { value: "USD", label: "USD" },
  { value: "VND", label: "VND" },
  { value: "EUR", label: "EUR" },
  { value: "SGD", label: "SGD" },
];

const billingCycleOptions = computed(() => [
  { value: "monthly", label: t("workspacePlan.billingCycle.monthly") },
  { value: "quarterly", label: t("workspacePlan.billingCycle.quarterly") },
  { value: "semi-annual", label: t("workspacePlan.billingCycle.semiAnnual") },
  { value: "annual", label: t("workspacePlan.billingCycle.annual") },
]);

const featureKeyOptions = [
  "app",
  "venue-map",
  "email-campaign",
  "promotion",
  "tracking-campaign",
  "custom-domain",
  "multi-language",
  "custom-content",
  "payment-provider",
  "market",
  "post",
  "site",
].map((value) => ({ value, label: value }));

const limitKeyOptions = [
  "events",
  "products",
  "users",
  "domains",
  "markets",
  "posts",
  "assets",
  "emails",
].map((value) => ({ value, label: value }));

const unitOptions = [
  { value: "count", label: "count" },
  { value: "mb", label: "mb" },
];

const windowOptions = [
  { value: "total", label: "total" },
  { value: "daily", label: "daily" },
  { value: "monthly", label: "monthly" },
];

const activeTab = ref("general");
const tabItems = computed(() => [
  { value: "general", label: t("workspacePlan.tab.general") },
  { value: "entitlements", label: t("workspacePlan.tab.entitlements") },
]);

watch(
  () => (formModel as any).key,
  (val) => {
    if (!val) return;
    const normalized = deburr(String(val)).trim().replace(/\s+/g, "").toUpperCase();
    if (normalized !== val) (formModel as any).key = normalized;
  },
);

function addPrice() {
  const prices = ((formModel as any).prices ||= []);
  prices.push({ billingCycle: "monthly", price: 0, airwallexPriceID: "" });
}

function removePrice(i: number) {
  ((formModel as any).prices as PlanPrice[]).splice(i, 1);
}

function addFeature() {
  const features = ((formModel as any).features ||= []);
  features.push({ key: "", enabled: false, allowedValues: [], isVisible: true, isFeatured: false, position: features.length + 1 });
}

function removeFeature(i: number) {
  ((formModel as any).features as PlanFeature[]).splice(i, 1);
}

function setAllowedValues(feature: any, val: string) {
  feature.allowedValues = val
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function addLimit() {
  const limits = ((formModel as any).limits ||= []);
  limits.push({ key: "", value: 0, unit: "count", window: "total", isVisible: true, position: limits.length + 1 });
}

function removeLimit(i: number) {
  ((formModel as any).limits as PlanLimit[]).splice(i, 1);
}

async function open(id?: string) {
  reset();
  activeTab.value = "general";
  if (id) {
    try {
      await load(id);
    } catch {
      if (errorMsg.value) toast.error({ title: errorMsg.value });
      return;
    }
  }
  isOpen.value = true;
}

async function submit() {
  const isCreating = isCreate.value;
  try {
    await save();
    isOpen.value = false;
    toast.success({
      title: t(isCreating ? "message.createSuccess" : "message.updateSuccess"),
    });
    emit("saved");
  } catch {
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="isCreate ? t('workspacePlan.add') : t('workspacePlan.edit')"
    size="xl"
    @close="isOpen = false"
  >
    <div class="flex flex-col gap-medium">
      <Tabs v-model="activeTab" :items="tabItems">
        <!-- General -->
        <template #general>
          <div class="flex flex-col gap-medium margin-top-medium">
        <FormField
          v-model="(formModel as any).name"
          :label="t('field.name')"
          :error="formErrors.name"
          required
        />

        <FormField
          v-model="(formModel as any).key"
          :label="t('field.key')"
          :error="formErrors.key"
          required
        />

        <div class="flex gap-medium">
          <FormSelect
            v-model="(formModel as any).type"
            :label="t('field.type')"
            :options="typeOptions"
            class="flex-1"
          />
          <FormSelect
            v-model="(formModel as any).kind"
            :label="t('workspacePlan.field.kind')"
            :options="kindOptions"
            class="flex-1"
          />
        </div>

        <FormNumber
          v-model="(formModel as any).position"
          :label="t('workspacePlan.field.position')"
        />

        <div class="flex gap-medium">
          <FormSwitch v-model="(formModel as any).isDefault" :label="t('workspacePlan.field.isDefault')" />
          <FormSwitch v-model="(formModel as any).isPublic" :label="t('workspacePlan.field.isPublic')" />
        </div>

        <Divider />

        <span class="text-body-secondary content-neutral-strong">{{ t("workspacePlan.trialTitle") }}</span>

        <FormNumber
          v-model="(formModel as any).trialDays"
          :label="t('workspacePlan.field.trialDays')"
        />

        <Divider />

        <span class="text-body-secondary content-neutral-strong">{{ t("workspacePlan.pricingTitle") }}</span>

        <FormSelect
          v-model="(formModel as any).currency"
          :label="t('workspacePlan.field.currency')"
          :options="currencyOptions"
        />

        <FormSelect
          v-model="(formModel as any).defaultBillingCycle"
          :label="t('workspacePlan.field.defaultBillingCycle')"
          :options="billingCycleOptions"
        />

        <div
          v-for="(price, i) in ((formModel as any).prices as PlanPrice[])"
          :key="i"
          class="flex flex-col gap-small padding-medium bg-canvas radius-xsmall"
        >
          <div class="flex gap-small items-start">
            <FormSelect
              v-model="price.billingCycle"
              :label="t('workspacePlan.field.billingCycle')"
              :options="billingCycleOptions"
              class="flex-1"
            />
            <FormNumber
              v-model="price.price"
              :label="t('workspacePlan.field.price')"
              class="flex-1"
            />
            <ButtonIcon variant="text-neutral" size="small" @click="removePrice(i)">
              <Trash class="icon" />
            </ButtonIcon>
          </div>
          <FormField
            :model-value="(price as any).airwallexPriceID || ''"
            :label="t('workspacePlan.field.airwallexPriceID')"
            :help-text="t('workspacePlan.airwallexPriceIDNote')"
            readonly
          />
        </div>

        <Button variant="tertiary" size="small" @click="addPrice">
          <Plus class="icon" />
          {{ t("workspacePlan.addPrice") }}
        </Button>
          </div>
        </template>

        <!-- Features & limits -->
        <template #entitlements>
          <div class="flex flex-col gap-medium margin-top-medium">
        <span class="text-body-secondary content-neutral-strong">{{ t("workspacePlan.featuresTitle") }}</span>

        <div
          v-for="(feature, i) in ((formModel as any).features as PlanFeature[])"
          :key="'f' + i"
          class="flex flex-col gap-small padding-medium bg-canvas radius-xsmall"
        >
          <div class="flex gap-small items-start">
            <FormSelect
              v-model="(feature as any).key"
              :label="t('field.key')"
              :options="featureKeyOptions"
              class="flex-1"
            />
            <FormNumber
              v-model="(feature as any).position"
              :label="t('workspacePlan.field.position')"
              class="w-24"
            />
            <ButtonIcon variant="text-neutral" size="small" @click="removeFeature(i)">
              <Trash class="icon" />
            </ButtonIcon>
          </div>
          <FormField
            :model-value="((feature as any).allowedValues || []).join(', ')"
            :label="t('workspacePlan.field.allowedValues')"
            :help-text="t('workspacePlan.allowedValuesNote')"
            @update:model-value="setAllowedValues(feature, $event)"
          />
          <div class="flex gap-medium">
            <FormSwitch v-model="(feature as any).enabled" :label="t('workspacePlan.field.enabled')" />
            <FormSwitch v-model="(feature as any).isVisible" :label="t('workspacePlan.field.visible')" />
            <FormSwitch v-model="(feature as any).isFeatured" :label="t('workspacePlan.field.featured')" />
          </div>
        </div>

        <Button variant="tertiary" size="small" @click="addFeature">
          <Plus class="icon" />
          {{ t("workspacePlan.addFeature") }}
        </Button>

        <Divider />

        <span class="text-body-secondary content-neutral-strong">{{ t("workspacePlan.limitsTitle") }}</span>

        <div
          v-for="(limit, i) in ((formModel as any).limits as PlanLimit[])"
          :key="'l' + i"
          class="flex flex-col gap-small padding-medium bg-canvas radius-xsmall"
        >
          <div class="flex gap-small items-start">
            <FormSelect
              v-model="(limit as any).key"
              :label="t('field.key')"
              :options="limitKeyOptions"
              class="flex-1"
            />
            <FormNumber
              v-model="(limit as any).value"
              :label="t('workspacePlan.field.value')"
              :help-text="t('workspacePlan.valueNote')"
              class="flex-1"
            />
            <ButtonIcon variant="text-neutral" size="small" @click="removeLimit(i)">
              <Trash class="icon" />
            </ButtonIcon>
          </div>
          <div class="flex gap-small items-start">
            <FormSelect
              v-model="(limit as any).unit"
              :label="t('workspacePlan.field.unit')"
              :options="unitOptions"
              class="flex-1"
            />
            <FormSelect
              v-model="(limit as any).window"
              :label="t('workspacePlan.field.window')"
              :options="windowOptions"
              class="flex-1"
            />
            <FormNumber
              v-model="(limit as any).position"
              :label="t('workspacePlan.field.position')"
              class="w-24"
            />
          </div>
          <FormSwitch v-model="(limit as any).isVisible" :label="t('workspacePlan.field.visible')" />
        </div>

        <Button variant="tertiary" size="small" @click="addLimit">
          <Plus class="icon" />
          {{ t("workspacePlan.addLimit") }}
        </Button>
          </div>
        </template>
      </Tabs>

      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>
    </div>

    <template #actions>
      <Button variant="tertiary" size="medium" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="medium"
        :disabled="saveLoading"
        @click="submit"
      >
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
